import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import type { DealerLaunchSocketEventLogEntry } from 'app/pages/Header/types/dealerLaunchProgress';
import {
  useDealerLaunchSocketSubscription,
} from 'app/pages/Header/hooks/useDealerLaunchSocketSubscription';
import { DealerLaunchProgressModal } from 'app/pages/Header/components/DealerLaunchProgressModal';
import { reloadAppAndRedirectToMonitor } from 'app/pages/Header/utils/postLaunchDealerFlow';

type DealerLaunchProgressContextValue = {
  progressModalOpen: boolean;
  latestSocketEvent: DealerLaunchSocketEventLogEntry | null;
  awaitingMonitorRedirect: boolean;
  openProgressModal: () => void;
  closeProgressModal: () => void;
  redirectToMonitor: () => void;
};

const DealerLaunchProgressContext = createContext<DealerLaunchProgressContextValue | null>(
  null,
);

export function DealerLaunchProgressProvider({
  children,
}: {
  children: ReactNode;
}) {
  const selectedDealer = useSelector(selectDealerSelected);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [awaitingMonitorRedirect, setAwaitingMonitorRedirect] = useState(false);
  const [latestSocketEvent, setLatestSocketEvent] =
    useState<DealerLaunchSocketEventLogEntry | null>(null);

  const selectedDealerId =
    selectedDealer && typeof selectedDealer === 'object'
      ? selectedDealer.dealer || selectedDealer._id || null
      : null;

  const replaceSocketEvent = useCallback((entry: DealerLaunchSocketEventLogEntry) => {
    setLatestSocketEvent(entry);
  }, []);

  const handleLaunchRequestReceived = useCallback((jobId: string | null) => {
    if (jobId) {
      setLatestSocketEvent(null);
      setAwaitingMonitorRedirect(false);
    }
  }, []);

  const handleLaunchCompleted = useCallback(
    (status: 'COMPLETED' | 'FAILED') => {
      if (status === 'COMPLETED') {
        setAwaitingMonitorRedirect(true);
        setProgressModalOpen(true);
      }
    },
    [],
  );

  useDealerLaunchSocketSubscription(selectedDealerId, {
    onSocketEvent: replaceSocketEvent,
    onLaunchRequestReceived: handleLaunchRequestReceived,
    onLaunchCompleted: handleLaunchCompleted,
  });

  const openProgressModal = useCallback(() => {
    setProgressModalOpen(true);
  }, []);

  const closeProgressModal = useCallback(() => {
    setProgressModalOpen(false);
  }, []);

  const redirectToMonitor = useCallback(() => {
    reloadAppAndRedirectToMonitor();
  }, []);

  const value = useMemo(
    () => ({
      progressModalOpen,
      latestSocketEvent,
      awaitingMonitorRedirect,
      openProgressModal,
      closeProgressModal,
      redirectToMonitor,
    }),
    [
      progressModalOpen,
      latestSocketEvent,
      awaitingMonitorRedirect,
      openProgressModal,
      closeProgressModal,
      redirectToMonitor,
    ],
  );

  return (
    <DealerLaunchProgressContext.Provider value={value}>
      {children}
      <DealerLaunchProgressModal
        open={progressModalOpen}
        onClose={closeProgressModal}
        dealer={selectedDealer}
        latestSocketEvent={latestSocketEvent}
        awaitingMonitorRedirect={awaitingMonitorRedirect}
        onRedirectToMonitor={redirectToMonitor}
      />
    </DealerLaunchProgressContext.Provider>
  );
}

export function useDealerLaunchProgress(): DealerLaunchProgressContextValue {
  const context = useContext(DealerLaunchProgressContext);
  if (!context) {
    throw new Error(
      'useDealerLaunchProgress must be used within DealerLaunchProgressProvider',
    );
  }
  return context;
}
